import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {CircularProgress, TextField} from "@material-ui/core";
import axios from 'axios';
import {ServiceLocator} from "../../../Services/ServiceLocator";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPeopleCarry} from "@fortawesome/free-solid-svg-icons";
import {parseNumberInputValue} from '../../../utils/numberInputUtils';
import PartDisplay from "../../Common/PartDisplay";
import { getDerivedFamilyUnitCount } from '../../../helpers/bomFamilyQuantity';
import {
    formatBomExtendedCostDisplay,
    formatBomUnitCostDisplay,
    getBomLineExtendedCost,
    isBomLineImpliedPart,
} from '../../../helpers/bomCost';

import { groupItemsForBomTable } from '../../../helpers/bomLineOrder';
import { AuthenticationService } from "../../../Services/AuthenticationService";

// Utility function for formatting decimal quantities
const formatQuantity = (value) => {
    if (value === null || value === undefined) return 1;
    // If it's a whole number, display as integer, otherwise show decimals
    return value % 1 === 0 ? Math.floor(value) : value;
};

const styles = theme => ({
    wrapper: {
        margin: -5,
    },
});

class BillOfMaterials extends React.Component {
    state = {
        loading: true,
        user: {},
        users: [],
        customDiscount: 50,
        customMsrp: 0.00,
        expandedSubAssemblies: new Set(),
        expandedFamilies: new Set(),
        imageModalOpen: false,
        selectedImageUrl: null
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadData();
    }

    loadData = () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();
        if (!user || !user.userPermissions || !user.userPermissions.includes('Workshop')) {
            globalProps.history.push('/login');
            return;
        }

        Promise.all([self.loadAssemblies(), self.loadSubAssemblies(), self.loadParts()]).then(() => {
            self.setState({loading: false, user, permissions: user.userPermissions});
        });
    };

    loadAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllAssembliesExpanded().then((assemblies) => {
            assemblies = assemblies.sort(function (a, b) {
                if (a.assembly.assemblyId > b.assembly.assemblyId) return 1;
                if (b.assembly.assemblyId > a.assembly.assemblyId) return -1;
                return 0;
            });
            self.setState({assemblies});
        });
    };

    loadSubAssemblies = () => {
        const self = this;
        const assemblyService = ServiceLocator.Instance.getAssemblyService();

        return assemblyService.getAllSubAssembliesExpanded().then((subAssemblies) => {
            subAssemblies = subAssemblies.sort(function (a, b) {
                if (a.subAssembly.subAssemblyId > b.subAssembly.subAssemblyId) return 1;
                if (b.subAssembly.subAssemblyId > a.subAssembly.subAssemblyId) return -1;
                return 0;
            });
            self.setState({subAssemblies});
        });
    };

    loadParts = () => {
        const self = this;
        self.setState({loading: true});
        const partService = ServiceLocator.Instance.getPartService();

        return partService.getAllPartsExpanded().then((parts) => {
            parts = parts.sort(function (a, b) {
                if (a.part.partId < b.part.partId) return -1;
                if (a.part.partId > b.part.partId) return 1;
                return 0;
            });
            self.setState({parts});
        });
    };

    editAssembly = (assembly, subAssembly, newTab) => {
        if (newTab)
            window.open(`/admin/assemblies/${assembly ? assembly.assembly.id : subAssembly.subAssembly.id}`);
        else
            globalProps.history.push(`/admin/assemblies/${assembly ? assembly.assembly.id : subAssembly.subAssembly.id}`)
    };

    editPart = (partExpanded, newTab) => {
        if (newTab)
            window.open(`/admin/parts/${partExpanded.part.id}`);
        else
            globalProps.history.push(`/admin/parts/${partExpanded.part.id}`);
    };

    handleAssemblyChange = (event, newValue) => {
        this.setState({selectedAssembly: !newValue ? null : newValue.value});
    };

    toggleSubAssemblyExpansion = (subAssemblyId) => {
        const expandedSubAssemblies = new Set(this.state.expandedSubAssemblies);
        if (expandedSubAssemblies.has(subAssemblyId)) {
            expandedSubAssemblies.delete(subAssemblyId);
        } else {
            expandedSubAssemblies.add(subAssemblyId);
        }
        this.setState({ expandedSubAssemblies });
    };

    toggleFamilyExpansion = (familyId) => {
        const expandedFamilies = new Set(this.state.expandedFamilies);
        if (expandedFamilies.has(familyId)) {
            expandedFamilies.delete(familyId);
        } else {
            expandedFamilies.add(familyId);
        }
        this.setState({ expandedFamilies });
    };

    renderBomMemo = (bomNotes) => {
        const text = (bomNotes || '').trim();
        if (!text) return null;

        return (
            <div style={{
                marginTop: 16,
                marginBottom: 12,
                padding: '12px 14px',
                backgroundColor: '#fff8e1',
                border: '1px solid #ffe082',
                borderRadius: 6,
                textAlign: 'left'
            }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>BOM Memo</div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.45, color: '#333' }}>{text}</div>
            </div>
        );
    };

    openImageModal = (imageUrl) => {
        this.setState({
            imageModalOpen: true,
            selectedImageUrl: imageUrl
        });
    };

    closeImageModal = () => {
        this.setState({
            imageModalOpen: false,
            selectedImageUrl: null
        });
    };

    getFirstImageUrl = (imageURLs) => {
        if (!imageURLs || imageURLs.trim() === '') return null;
        const urls = imageURLs.split('~|~');
        return urls[0] || null;
    };

    renderSubAssemblyParts = (subAssembly, depth = 1) => {
        // Single ordered list across child subassemblies, parts, and families (matches Edit Assembly)
        const bomLines = groupItemsForBomTable(
            [],
            subAssembly.partsToSubAssemblies,
            subAssembly.childSubAssemblies || []
        );
        const partsHtml = [];

        bomLines.forEach((bomItem) => {
            if (bomItem.itemType === 'subassembly') {
                const childSubAssemblyToSubAssembly = bomItem;
                const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyToSubAssembly.childSubAssemblyFk);
                if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) return;
                const childSubAssembly = childSubAssemblyExpanded.subAssembly;
                const isExpanded = this.state.expandedSubAssemblies.has(childSubAssembly.id);

                const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
                const backgroundColor = depth % 2 === 1 ? '#e6f3ff' : '#f0f8ff'; // Alternate colors for depth

                // Calculate cost including nested subassemblies (recursive)
                const calculateSubAssemblyCost = (subAssembly) => {
                    let cost = 0;
                    // Add parts cost
                    subAssembly.partsToSubAssemblies.forEach((partToSubAssembly) => {
                        const expandedPart = this.state.parts.find(x => x.part.id === partToSubAssembly.partFk);
                        if (expandedPart && !isBomLineImpliedPart(partToSubAssembly)) {
                            cost += (expandedPart.cost / expandedPart.quantity) * partToSubAssembly.quantity;
                        }
                    });
                    // Add child subassemblies cost
                    if (subAssembly.childSubAssemblies) {
                        subAssembly.childSubAssemblies
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .forEach((childRelation) => {
                            const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childRelation.childSubAssemblyFk);
                            if (childSubAssemblyExpanded) {
                                cost += calculateSubAssemblyCost(childSubAssemblyExpanded.subAssembly) * childRelation.quantity;
                            }
                        });
                    }
                    return cost;
                };

                const totalUnitCost = calculateSubAssemblyCost(childSubAssembly);

                const childSubAssemblyRow = (
                    <tr key={`sub-${subAssembly.id}-child-${childSubAssembly.id}`} style={{backgroundColor}}>
                        <td style={{textAlign: 'center', padding: '4px'}}>
                            <span 
                                onClick={() => this.toggleSubAssemblyExpansion(childSubAssembly.id)}
                                style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                            >
                                {isExpanded ? '▼' : '▶'}
                            </span>
                        </td>
                        <td className="text-left" style={{paddingLeft: `${paddingLeft}px`, width: 'auto', minWidth: '200px'}}>
                            {this.state.permissions && this.state.permissions.includes('EditAssembliesAndParts') ? (
                                <div style={{cursor: 'pointer'}} onClick={(event) => this.editAssembly(null, childSubAssemblyExpanded, event.ctrlKey)}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{childSubAssembly.subAssemblyId}</span>
                                        <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{childSubAssembly.revision}
                                    </div>
                                    {childSubAssembly.description && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {childSubAssembly.description}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{childSubAssembly.subAssemblyId}</span>
                                        <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{childSubAssembly.revision}
                                    </div>
                                    {childSubAssembly.description && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {childSubAssembly.description}
                                        </div>
                                    )}
                                </div>
                            )}
                        </td>
                        <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(childSubAssemblyToSubAssembly.quantity)}</td>
                        <td>${totalUnitCost.toFixed(2)}</td>
                        <td>${(totalUnitCost * childSubAssemblyToSubAssembly.quantity).toFixed(2)}</td>
                    </tr>
                );

                partsHtml.push(childSubAssemblyRow);

                // Recursively render the child subassembly's parts and children if expanded
                if (isExpanded) {
                    const childParts = this.renderSubAssemblyParts(childSubAssembly, depth + 1);
                    partsHtml.push(...childParts);
                }
            } else if (bomItem.itemType === 'part') {
            const subAssemblyPart = bomItem;
            const expandedPart = this.state.parts.find(x => x.part.id === subAssemblyPart.partFk);
            if (!expandedPart || expandedPart.part.isActive === false) return;
            const part = expandedPart.part;

            const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
            partsHtml.push(
                <tr key={`sub-${subAssembly.id}-part-${subAssemblyPart.id}`} style={{backgroundColor: '#dbdbdb'}}>
                    <td></td>
                    <td className="text-left" style={{paddingLeft: `${paddingLeft}px`, width: 'auto', minWidth: '200px'}}>
                        {this.state.permissions && this.state.permissions.includes('EditAssembliesAndParts') ? (
                            <div style={{cursor: 'pointer'}} onClick={(event) => this.editPart(expandedPart, event.ctrlKey)}>
                                <PartDisplay part={part} variant="table" classes={this.props.classes} />
                            </div>
                        ) : (
                            <PartDisplay part={part} variant="table" classes={this.props.classes} />
                        )}
                    </td>
                    <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyPart.quantity)}</td>
                    <td>{formatBomUnitCostDisplay(subAssemblyPart, expandedPart)}</td>
                    <td>{formatBomExtendedCostDisplay(subAssemblyPart, expandedPart, subAssemblyPart.quantity)}</td>
                </tr>
            );
            } else if (bomItem.itemType === 'family') {
            const family = bomItem;
            // Calculate family cost (always include in total, regardless of expansion)
            let familyTotalCost = 0.00;
            family.parts.forEach((subAssemblyPart) => {
                const expandedPart = this.state.parts.find(x => x.part.id === subAssemblyPart.partFk);
                if (!expandedPart || expandedPart.part.isActive === false) return;
                
                if (!isBomLineImpliedPart(subAssemblyPart)) {
                    const extCost = getBomLineExtendedCost(subAssemblyPart, expandedPart, subAssemblyPart.quantity);
                    if (extCost != null) familyTotalCost += extCost;
                }
            });

            const subFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);

            // Family header row
            partsHtml.push(
                <tr key={`sub-${subAssembly.id}-family-${family.familyId}`} style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                    <td style={{textAlign: 'center', padding: '4px'}}>
                        <span 
                            onClick={() => this.toggleFamilyExpansion(family.familyId)}
                            style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                        >
                            {this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId) ? '▼' : '▶'}
                        </span>
                    </td>
                    <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                        <div style={{display: 'flex', alignItems: 'baseline'}}>
                            <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{family.familyName}<FontAwesomeIcon icon={faPeopleCarry} style={{ marginLeft: '6px' }} /></span>
                        </div>
                        {family.familyDescription && (
                            <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                {family.familyDescription}
                            </div>
                        )}
                    </td>
                    <td style={{width: '60px', textAlign: 'center'}}>{subFamilyUnitCount != null ? formatQuantity(subFamilyUnitCount) : ''}</td>
                    <td>${familyTotalCost.toFixed(2)}</td>
                    <td>${familyTotalCost.toFixed(2)}</td>
                </tr>
            );

            // Family parts - only show if expanded
            if (this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId)) {
                family.parts.forEach((subAssemblyPart) => {
                const expandedPart = this.state.parts.find(x => x.part.id === subAssemblyPart.partFk);
                if (!expandedPart || expandedPart.part.isActive === false) return;
                const part = expandedPart.part;

                const paddingLeft = 5 + (depth * 10); // Increase padding based on depth
                partsHtml.push(
                    <tr key={`sub-${subAssembly.id}-family-${family.familyId}-part-${subAssemblyPart.id}`} style={{backgroundColor: '#f0f8ff'}}>
                        <td></td>
                        <td className="text-left" style={{paddingLeft: `${paddingLeft}px`, width: 'auto', minWidth: '200px'}}>
                            {this.state.permissions && this.state.permissions.includes('EditAssembliesAndParts') ? (
                                <div style={{cursor: 'pointer'}} onClick={(event) => this.editPart(expandedPart, event.ctrlKey)}>
                                    <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                </div>
                            ) : (
                                <PartDisplay part={part} variant="table" classes={this.props.classes} />
                            )}
                        </td>
                        <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyPart.quantity)}</td>
                        <td>{formatBomUnitCostDisplay(subAssemblyPart, expandedPart)}</td>
                        <td>{formatBomExtendedCostDisplay(subAssemblyPart, expandedPart, subAssemblyPart.quantity)}</td>
                    </tr>
                );
            });
            }
            }
        });

        return partsHtml;
    };

    groupPartsByFamily = (partsToAssemblies) => {
        const families = {};
        const individualParts = [];

        partsToAssemblies.forEach((partToAssembly) => {
            if (partToAssembly.partFamilyId) {
                if (!families[partToAssembly.partFamilyId]) {
                    families[partToAssembly.partFamilyId] = {
                        familyId: partToAssembly.partFamilyId,
                        familyName: partToAssembly.partFamilyName,
                        familyDescription: partToAssembly.partFamilyDescription,
                        familyImageURL: partToAssembly.partFamilyImageURL,
                        parts: []
                    };
                }
                families[partToAssembly.partFamilyId].parts.push(partToAssembly);
            } else {
                individualParts.push(partToAssembly);
            }
        });

        // Sort parts within each family by order
        Object.values(families).forEach(family => {
            family.parts.sort((a, b) => (a.order || 0) - (b.order || 0));
        });

        return {
            families: Object.values(families),
            individualParts
        };
    };

    render() {
        if (this.state.loading) {
            return (
                <DashboardTemplate title="Bill Of Materials" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        let assemblyOptions = [];

        this.state.subAssemblies.filter(x => x.subAssembly.isActive).forEach((assembly) => {
            const packagingIcon = assembly.subAssembly.currentlyPackaging ? ' 📦' : '';
            assemblyOptions.push({title: `${assembly.subAssembly.subAssemblyId} rev ${assembly.subAssembly.revision}${packagingIcon}`, value: assembly.subAssembly.id});
        });

        this.state.assemblies.filter(x => x.assembly.isActive).forEach((assembly) => {
            const packagingIcon = assembly.assembly.currentlyPackaging ? ' 📦' : '';
            const sellingIcon = assembly.assembly.finalOrderNumber ? '' : ' 🛒'; // Currently selling if no finalOrderNumber
            assemblyOptions.push({title: `${assembly.assembly.assemblyId} rev ${assembly.assembly.revision}${packagingIcon}${sellingIcon}`, value: assembly.assembly.id});
        });

        let assemblyListHtml = [];
        let assemblyCostAnalysisHtml = [];
        if (this.state.selectedAssembly) {
            let assemblyExpandedMaybe = this.state.assemblies.find(x => x.assembly.id === this.state.selectedAssembly);
            let assemblyMaybe = assemblyExpandedMaybe ? assemblyExpandedMaybe.assembly : null;
            let subAssemblyExpandedMaybe = this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly);
            let subAssemblyMaybe = !assemblyMaybe && subAssemblyExpandedMaybe ? this.state.subAssemblies.find(x => x.subAssembly.id === this.state.selectedAssembly).subAssembly : null;

            if (assemblyMaybe) {
                let totalPartCost = 0;
                const bomLines = groupItemsForBomTable(
                    assemblyMaybe.subAssembliesToAssemblies,
                    assemblyMaybe.partsToAssemblies
                );
                const rowsHtml = [];

                bomLines.forEach((bomItem) => {
                    if (bomItem.itemType === 'subassembly') {
                    const subAssemblyToAssembly = bomItem;
                    const subAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === subAssemblyToAssembly.subAssemblyFk);
                    const subAssembly = subAssemblyExpanded.subAssembly;
                    const isExpanded = this.state.expandedSubAssemblies.has(subAssembly.id);

                    let totalUnitCost = 0.00;
                    subAssembly.partsToSubAssemblies.forEach((partToSubAssembly) => {
                        const expandedPart = this.state.parts.find(x => x.part.id === partToSubAssembly.partFk);
                        if (expandedPart && !isBomLineImpliedPart(partToSubAssembly)) {
                            totalUnitCost += (expandedPart.cost / expandedPart.quantity) * partToSubAssembly.quantity;
                        }
                    });

                    totalPartCost += totalUnitCost * subAssemblyToAssembly.quantity;

                    const subAssemblyRow = (
                        <tr key={`subassembly-${subAssembly.id}`}>
                            <td style={{textAlign: 'center', padding: '4px'}}>
                                <span 
                                    onClick={() => this.toggleSubAssemblyExpansion(subAssembly.id)}
                                    style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                >
                                    {isExpanded ? '▼' : '▶'}
                                </span>
                            </td>
                            <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                {this.state.permissions && this.state.permissions.includes('EditAssembliesAndParts') ? (
                                    <div style={{cursor: 'pointer'}} onClick={(event) => this.editAssembly(null, subAssemblyExpanded, event.ctrlKey)}>
                                        <div style={{display: 'flex', alignItems: 'baseline'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{subAssembly.subAssemblyId}</span>
                                            <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{subAssembly.revision}
                                        </div>
                                        {subAssembly.description && (
                                            <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                                {subAssembly.description}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{display: 'flex', alignItems: 'baseline'}}>
                                            <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{subAssembly.subAssemblyId}</span>
                                            <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{subAssembly.revision}
                                        </div>
                                        {subAssembly.description && (
                                            <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                                {subAssembly.description}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </td>
                            <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(subAssemblyToAssembly.quantity)}</td>
                            <td>${totalUnitCost.toFixed(2)}</td>
                            <td>${(totalUnitCost * subAssemblyToAssembly.quantity).toFixed(2)}</td>
                        </tr>
                    );

                    const subAssemblyParts = isExpanded ? this.renderSubAssemblyParts(subAssembly) : [];

                    rowsHtml.push(subAssemblyRow, ...subAssemblyParts);
                    } else if (bomItem.itemType === 'part') {
                        const assemblyPart = bomItem;
                        const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                        if (!expandedPart || expandedPart.part.isActive === false) return;
                        const part = expandedPart.part;

                        if (!isBomLineImpliedPart(assemblyPart)) {
                            const extCost = getBomLineExtendedCost(assemblyPart, expandedPart, assemblyPart.quantity);
                            if (extCost != null) totalPartCost += extCost;
                        }
                        rowsHtml.push(
                            <tr key={`assembly-part-${assemblyPart.id}`}>
                                <td style={{textAlign: 'center', padding: '4px'}}></td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    {this.state.permissions.includes('EditAssembliesAndParts') ? (
                                        <div style={{cursor: 'pointer'}} onClick={(event) => this.editPart(expandedPart, event.ctrlKey)}>
                                            <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                        </div>
                                    ) : (
                                        <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                    )}
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                <td>{formatBomUnitCostDisplay(assemblyPart, expandedPart)}</td>
                                <td>{formatBomExtendedCostDisplay(assemblyPart, expandedPart, assemblyPart.quantity)}</td>
                            </tr>
                        );
                    } else if (bomItem.itemType === 'family') {
                        const family = bomItem;
                        
                        // Calculate family cost (always include in total, regardless of expansion)
                        let familyTotalCost = 0.00;
                        family.parts.forEach((assemblyPart) => {
                            const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                            if (!expandedPart || expandedPart.part.isActive === false) return;
                            
                            if (!isBomLineImpliedPart(assemblyPart)) {
                                const extCost = getBomLineExtendedCost(assemblyPart, expandedPart, assemblyPart.quantity);
                                if (extCost != null) familyTotalCost += extCost;
                            }
                        });
                        totalPartCost += familyTotalCost;

                        const assemblyFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);

                        // Family header row
                        rowsHtml.push(
                            <tr key={`assembly-family-${family.familyId}`} style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleFamilyExpansion(family.familyId)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId) ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{family.familyName}<FontAwesomeIcon icon={faPeopleCarry} style={{ marginLeft: '6px' }} /></span>
                                    </div>
                                    {family.familyDescription && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {family.familyDescription}
                                        </div>
                                    )}
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{assemblyFamilyUnitCount != null ? formatQuantity(assemblyFamilyUnitCount) : ''}</td>
                                <td>${familyTotalCost.toFixed(2)}</td>
                                <td>${familyTotalCost.toFixed(2)}</td>
                            </tr>
                        );

                        // Family parts - only show if expanded
                        if (this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId)) {
                            family.parts.forEach((assemblyPart) => {
                            const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                            if (!expandedPart || expandedPart.part.isActive === false) return;
                            const part = expandedPart.part;
                            rowsHtml.push(
                                <tr key={`assembly-family-${family.familyId}-part-${assemblyPart.id}`} style={{backgroundColor: '#f0f8ff'}}>
                                    <td></td>
                                    <td className="text-left" style={{paddingLeft: '15px', width: 'auto', minWidth: '200px'}}>
                                        {this.state.permissions.includes('EditAssembliesAndParts') ? (
                                            <div style={{cursor: 'pointer'}} onClick={(event) => this.editPart(expandedPart, event.ctrlKey)}>
                                                <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                            </div>
                                        ) : (
                                            <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                        )}
                                    </td>
                                    <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                    <td>{formatBomUnitCostDisplay(assemblyPart, expandedPart)}</td>
                                    <td>{formatBomExtendedCostDisplay(assemblyPart, expandedPart, assemblyPart.quantity)}</td>
                                </tr>
                            );
                        });
                        }
                    }
                });

                // Main BOM Table Container (always shows)
                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        {this.renderBomMemo(assemblyMaybe.bomNotes)}
                        <div className="parts-table-container">
                            <table className="parts-table" style={{minWidth: '100%'}}>
                                <thead>
                                <tr style={{backgroundColor: "#000 !important", color: "#FFF !important"}}>
                                    <th style={{width: '30px'}}></th>
                                    <th style={{width: 'auto', minWidth: '200px'}}>Part Number + Description</th>
                                    <th style={{width: '40px'}}>Qty</th>
                                    <th style={{width: '60px'}}>Unit Cost</th>
                                    <th style={{width: '60px'}}>Ext. Cost</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rowsHtml}
                                </tbody>
                            </table>
                        </div>
                        <div style={{margin: '20px 0'}}>
                            <b>Total Part Cost: ${totalPartCost.toFixed(2)}</b>
                        </div>
                    </div>
                );
                
                // Calculations Container - Only for Assemblies
                assemblyCostAnalysisHtml.push(
                    <div className="container" style={{margin: '0 0 0 20px', maxWidth: 400}}>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                            {/* MSRP Section */}
                            <table className="parts-table" style={{minWidth: 100}}>
                                <tbody>
                                <tr style={{backgroundColor: '#bcdfbc'}}>
                                    <td style={{fontWeight: 'bold', width: '60%'}}>MSRP</td>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>${assemblyMaybe.msrp.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Shipping Cost</td>
                                    <td style={{width: '40%'}}>${assemblyMaybe.shippingCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Transaction Fees</td>
                                    <td style={{width: '40%'}}>${(assemblyMaybe.msrp * 0.03).toFixed(2)}</td>
                                </tr>
                                <tr style={{backgroundColor: '#bcdfbc'}}>
                                    <td style={{fontWeight: 'bold', width: '60%'}}>Retail Profit</td>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>${(assemblyMaybe.msrp - (assemblyMaybe.shippingCost + (assemblyMaybe.msrp * 0.03) + totalPartCost)).toFixed(2)}&nbsp;({((1 - ((totalPartCost + (assemblyMaybe.msrp * 0.03) + assemblyMaybe.shippingCost) / assemblyMaybe.msrp)) * 100).toFixed(0)}%)</td>
                                </tr>
                                </tbody>
                            </table>
                            
                            {/* Discount Tier 1 */}
                            <table className="parts-table" style={{minWidth: 100}}>
                                <thead>
                                <tr style={{backgroundColor: '#000', color: '#FFF'}}>
                                    <th style={{width: '60%'}}>Discount Tier 1</th>
                                    <th style={{width: '40%'}}>{assemblyMaybe.discountTier1 * 100}%</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr style={{backgroundColor: '#F4E4BC'}}>
                                    <td style={{width: '60%'}}>Price Tier 1</td>
                                    <td style={{width: '40%'}}>${(assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier1)).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Shipping Cost</td>
                                    <td style={{width: '40%'}}>${assemblyMaybe.shippingCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Transaction Fees</td>
                                    <td style={{width: '40%'}}>${((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier1)) * 0.03).toFixed(2)}</td>
                                </tr>
                                <tr style={{backgroundColor: '#F4E4BC'}}>
                                    <td style={{fontWeight: 'bold', width: '60%'}}>Wholesale Profit</td>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>${((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier1)) - (assemblyMaybe.shippingCost + ((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier1)) * 0.03) + totalPartCost)).toFixed(2)}&nbsp;({((1 - ((totalPartCost + ((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier1)) * 0.03) + assemblyMaybe.shippingCost) / (assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier1)))) * 100).toFixed(0)}%)</td>
                                </tr>
                                </tbody>
                            </table>
                            
                            {/* Discount Tier 2 */}
                            <table className="parts-table" style={{minWidth: 100}}>
                                <thead>
                                <tr style={{backgroundColor: '#000', color: '#FFF'}}>
                                    <th style={{width: '60%'}}>Discount Tier 2</th>
                                    <th style={{width: '40%'}}>{assemblyMaybe.discountTier2 * 100}%</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr style={{backgroundColor: '#ffe4e4'}}>
                                    <td style={{width: '60%'}}>Price Tier 2</td>
                                    <td style={{width: '40%'}}>${(assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier2)).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Shipping Cost</td>
                                    <td style={{width: '40%'}}>${assemblyMaybe.shippingCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Transaction Fees</td>
                                    <td style={{width: '40%'}}>${((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier2)) * 0.03).toFixed(2)}</td>
                                </tr>
                                <tr style={{backgroundColor: '#ffe4e4'}}>
                                    <td style={{fontWeight: 'bold', width: '60%'}}>Wholesale Profit</td>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>${((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier2)) - (assemblyMaybe.shippingCost + ((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier2)) * 0.03) + totalPartCost)).toFixed(2)}&nbsp;({((1 - ((totalPartCost + ((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier2)) * 0.03) + assemblyMaybe.shippingCost) / (assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier2)))) * 100).toFixed(0)}%)</td>
                                </tr>
                                </tbody>
                            </table>
                            
                            {/* Discount Tier 3 */}
                            <table className="parts-table" style={{minWidth: 100}}>
                                <thead>
                                <tr style={{backgroundColor: '#000', color: '#FFF'}}>
                                    <th style={{width: '60%'}}>Discount Tier 3</th>
                                    <th style={{width: '40%'}}>{assemblyMaybe.discountTier3 * 100}%</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr style={{backgroundColor: '#F4A460'}}>
                                    <td style={{width: '60%'}}>Price Tier 3</td>
                                    <td style={{width: '40%'}}>${(assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier3)).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Shipping Cost</td>
                                    <td style={{width: '40%'}}>${assemblyMaybe.shippingCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Transaction Fees</td>
                                    <td style={{width: '40%'}}>${((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier3)) * 0.03).toFixed(2)}</td>
                                </tr>
                                <tr style={{backgroundColor: '#F4A460'}}>
                                    <td style={{fontWeight: 'bold', width: '60%'}}>Wholesale Profit</td>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>${((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier3)) - (assemblyMaybe.shippingCost + ((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier3)) * 0.03) + totalPartCost)).toFixed(2)}&nbsp;({((1 - ((totalPartCost + ((assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier3)) * 0.03) + assemblyMaybe.shippingCost) / (assemblyMaybe.msrp * (1 - assemblyMaybe.discountTier3)))) * 100).toFixed(0)}%)</td>
                                </tr>
                                </tbody>
                            </table>
                            
                            {/* Custom Discount */}
                            <table className="parts-table" style={{minWidth: 100}}>
                                <thead>
                                <tr style={{backgroundColor: '#000', color: '#FFF'}}>
                                    <th style={{width: '60%'}}>Custom Discount</th>
                                    <th style={{width: '40%'}}><TextField value={this.state.customDiscount || ''} onChange={(event) => {
                                        this.setState({customDiscount: event.target.value})
                                    }} style={{width: 60, backgroundColor: '#FFF', color: '#000', paddingLeft: '8px', paddingRight: '8px'}} type="number"/>&nbsp;%
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr style={{backgroundColor: '#ADD8E6'}}>
                                    <td style={{width: '60%'}}>Custom Price</td>
                                    <td style={{width: '40%'}}>${(assemblyMaybe.msrp * (1 - (parseNumberInputValue(this.state.customDiscount) / 100))).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Shipping Cost</td>
                                    <td style={{width: '40%'}}>${assemblyMaybe.shippingCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Transaction Fees</td>
                                    <td style={{width: '40%'}}>${((assemblyMaybe.msrp * (1 - (parseNumberInputValue(this.state.customDiscount) / 100))) * 0.03).toFixed(2)}</td>
                                </tr>
                                <tr style={{backgroundColor: '#ADD8E6'}}>
                                    <td style={{fontWeight: 'bold', width: '60%'}}>Wholesale Profit</td>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>${((assemblyMaybe.msrp * (1 - (parseNumberInputValue(this.state.customDiscount) / 100))) - (assemblyMaybe.shippingCost + ((assemblyMaybe.msrp * (1 - (parseNumberInputValue(this.state.customDiscount) / 100))) * 0.03) + totalPartCost)).toFixed(2)}&nbsp;({((1 - ((totalPartCost + ((assemblyMaybe.msrp * (1 - (parseNumberInputValue(this.state.customDiscount) / 100))) * 0.03) + assemblyMaybe.shippingCost) / (assemblyMaybe.msrp * (1 - (parseNumberInputValue(this.state.customDiscount) / 100))))) * 100).toFixed(0)}%)</td>
                                </tr>
                                </tbody>
                            </table>
                            
                            {/* Custom MSRP */}
                            <table className="parts-table" style={{minWidth: 100}}>
                                <thead>
                                <tr style={{backgroundColor: '#000', color: '#FFF'}}>
                                    <th style={{width: '60%'}}>Custom MSRP</th>
                                    <th style={{width: '40%'}}>$&nbsp;<TextField value={this.state.customMsrp || ''} onChange={(event) => {
                                        this.setState({customMsrp: event.target.value})
                                    }} style={{width: 60, backgroundColor: '#FFF', color: '#000', paddingLeft: '8px', paddingRight: '8px'}} type="number"/>
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr style={{backgroundColor: '#ADD8E6'}}>
                                    <td style={{width: '60%'}}>Custom Price</td>
                                    <td style={{width: '40%'}}>${parseNumberInputValue(this.state.customMsrp).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Shipping Cost</td>
                                    <td style={{width: '40%'}}>${assemblyMaybe.shippingCost.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={{width: '60%'}}>Transaction Fees</td>
                                    <td style={{width: '40%'}}>${(parseNumberInputValue(this.state.customMsrp) * 0.03).toFixed(2)}</td>
                                </tr>
                                <tr style={{backgroundColor: '#ADD8E6'}}>
                                    <td style={{fontWeight: 'bold', width: '60%'}}>Wholesale Profit</td>
                                    <td style={{fontWeight: 'bold', width: '40%'}}>${(parseNumberInputValue(this.state.customMsrp) - (assemblyMaybe.shippingCost + (parseNumberInputValue(this.state.customMsrp) * 0.03) + totalPartCost)).toFixed(2)}&nbsp;({((1 - ((totalPartCost + parseNumberInputValue(this.state.customMsrp) * 0.03) + assemblyMaybe.shippingCost) / parseNumberInputValue(this.state.customMsrp)) * 100).toFixed(0)}%)</td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            } else {
                let totalPartCost = 0;
                
                // Single ordered list across child subassemblies, parts, and families (matches Edit Assembly)
                const bomLines = groupItemsForBomTable(
                    [],
                    subAssemblyMaybe.partsToSubAssemblies,
                    subAssemblyMaybe.childSubAssemblies || []
                );
                const rowsHtml = [];

                bomLines.forEach((bomItem) => {
                    if (bomItem.itemType === 'subassembly') {
                        const childSubAssemblyToSubAssembly = bomItem;
                        const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childSubAssemblyToSubAssembly.childSubAssemblyFk);
                        if (!childSubAssemblyExpanded || !childSubAssemblyExpanded.subAssembly.isActive) return;
                        const childSubAssembly = childSubAssemblyExpanded.subAssembly;
                        const isExpanded = this.state.expandedSubAssemblies.has(childSubAssembly.id);

                        let totalUnitCost = 0.00;
                        // Calculate cost including nested subassemblies
                        const calculateSubAssemblyCost = (subAssembly) => {
                            let cost = 0;
                            // Add parts cost
                            subAssembly.partsToSubAssemblies.forEach((partToSubAssembly) => {
                                const expandedPart = this.state.parts.find(x => x.part.id === partToSubAssembly.partFk);
                                if (expandedPart && !isBomLineImpliedPart(partToSubAssembly)) {
                                    cost += (expandedPart.cost / expandedPart.quantity) * partToSubAssembly.quantity;
                                }
                            });
                            // Add child subassemblies cost
                            if (subAssembly.childSubAssemblies) {
                                subAssembly.childSubAssemblies
                                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                                    .forEach((childRelation) => {
                                    const childSubAssemblyExpanded = this.state.subAssemblies.find(x => x.subAssembly.id === childRelation.childSubAssemblyFk);
                                    if (childSubAssemblyExpanded) {
                                        cost += calculateSubAssemblyCost(childSubAssemblyExpanded.subAssembly) * childRelation.quantity;
                                    }
                                });
                            }
                            return cost;
                        };

                        totalUnitCost = calculateSubAssemblyCost(childSubAssembly);
                        totalPartCost += totalUnitCost * childSubAssemblyToSubAssembly.quantity;

                        const childSubAssemblyRow = (
                            <tr key={`child-subassembly-${childSubAssembly.id}`}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleSubAssemblyExpansion(childSubAssembly.id)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {isExpanded ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    {this.state.permissions && this.state.permissions.includes('EditAssembliesAndParts') ? (
                                        <div style={{cursor: 'pointer'}} onClick={(event) => this.editAssembly(null, childSubAssemblyExpanded, event.ctrlKey)}>
                                            <div style={{display: 'flex', alignItems: 'baseline'}}>
                                                <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{childSubAssembly.subAssemblyId}</span>
                                                <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{childSubAssembly.revision}
                                            </div>
                                            {childSubAssembly.description && (
                                                <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                                    {childSubAssembly.description}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div>
                                            <div style={{display: 'flex', alignItems: 'baseline'}}>
                                                <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{childSubAssembly.subAssemblyId}</span>
                                                <span style={{fontSize: '14px', color: '#00acb5', fontWeight: 'bold'}}>rev</span>&nbsp;{childSubAssembly.revision}
                                            </div>
                                            {childSubAssembly.description && (
                                                <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                                    {childSubAssembly.description}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(childSubAssemblyToSubAssembly.quantity)}</td>
                                <td>${totalUnitCost.toFixed(2)}</td>
                                <td>${(totalUnitCost * childSubAssemblyToSubAssembly.quantity).toFixed(2)}</td>
                            </tr>
                        );

                        const childSubAssemblyParts = isExpanded ? this.renderSubAssemblyParts(childSubAssembly) : [];

                        rowsHtml.push(childSubAssemblyRow, ...childSubAssemblyParts);
                    } else if (bomItem.itemType === 'part') {
                    const assemblyPart = bomItem;
                    const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);

                    if (!expandedPart || expandedPart.part.isActive === false) return;
                    const part = expandedPart.part;

                    const extCost = getBomLineExtendedCost(assemblyPart, expandedPart, assemblyPart.quantity);
                    if (extCost != null) totalPartCost += extCost;

                    rowsHtml.push(
                        <tr key={`subassembly-part-${assemblyPart.id}`}>
                            <td></td>
                            <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                {this.state.permissions.includes('EditAssembliesAndParts') ? (
                                    <div style={{cursor: 'pointer'}} onClick={(event) => this.editPart(expandedPart, event.ctrlKey)}>
                                        <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                    </div>
                                ) : (
                                    <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                )}
                            </td>
                            <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                            <td>{formatBomUnitCostDisplay(assemblyPart, expandedPart)}</td>
                            <td>{formatBomExtendedCostDisplay(assemblyPart, expandedPart, assemblyPart.quantity)}</td>
                        </tr>
                    );
                    } else if (bomItem.itemType === 'family') {
                        const family = bomItem;

                        // Calculate family cost (always include in total, regardless of expansion)
                        let familyTotalCost = 0.00;
                        family.parts.forEach((assemblyPart) => {
                            const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                            if (!expandedPart || expandedPart.part.isActive === false) return;

                            if (!isBomLineImpliedPart(assemblyPart)) {
                                const extCost = getBomLineExtendedCost(assemblyPart, expandedPart, assemblyPart.quantity);
                                if (extCost != null) familyTotalCost += extCost;
                            }
                        });
                        totalPartCost += familyTotalCost;

                        const subRootFamilyUnitCount = getDerivedFamilyUnitCount(family, this.state.parts);

                        // Family header row
                        rowsHtml.push(
                            <tr key={`subassembly-family-${family.familyId}`} style={{backgroundColor: '#e6f3ff', fontWeight: 'bold'}}>
                                <td style={{textAlign: 'center', padding: '4px'}}>
                                    <span 
                                        onClick={() => this.toggleFamilyExpansion(family.familyId)}
                                        style={{cursor: 'pointer', userSelect: 'none', fontSize: '14px'}}
                                    >
                                        {this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId) ? '▼' : '▶'}
                                    </span>
                                </td>
                                <td className="text-left" style={{width: 'auto', minWidth: '200px'}}>
                                    <div style={{display: 'flex', alignItems: 'baseline'}}>
                                        <span style={{fontWeight: 'bold', fontSize: '14px', color: '#333', marginRight: '4px'}}>{family.familyName}<FontAwesomeIcon icon={faPeopleCarry} style={{ marginLeft: '6px' }} /></span>
                                    </div>
                                    {family.familyDescription && (
                                        <div style={{fontSize: '11px', color: '#555', lineHeight: '1.3', wordWrap: 'break-word', overflowWrap: 'break-word', hyphens: 'auto'}}>
                                            {family.familyDescription}
                                        </div>
                                    )}
                                </td>
                                <td style={{width: '60px', textAlign: 'center'}}>{subRootFamilyUnitCount != null ? formatQuantity(subRootFamilyUnitCount) : ''}</td>
                                <td>${familyTotalCost.toFixed(2)}</td>
                                <td>${familyTotalCost.toFixed(2)}</td>
                            </tr>
                        );

                        // Family parts - only show if expanded
                        if (this.state.expandedFamilies && this.state.expandedFamilies.has(family.familyId)) {
                            family.parts.forEach((assemblyPart) => {
                                const expandedPart = this.state.parts.find(x => x.part.id === assemblyPart.partFk);
                                if (!expandedPart || expandedPart.part.isActive === false) return;
                                const part = expandedPart.part;
                                rowsHtml.push(
                                    <tr key={`subassembly-family-${family.familyId}-part-${assemblyPart.id}`} style={{backgroundColor: '#f0f8ff'}}>
                                        <td></td>
                                        <td className="text-left" style={{paddingLeft: '15px', width: 'auto', minWidth: '200px'}}>
                                            {this.state.permissions.includes('EditAssembliesAndParts') ? (
                                                <div style={{cursor: 'pointer'}} onClick={(event) => this.editPart(expandedPart, event.ctrlKey)}>
                                                    <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                                </div>
                                            ) : (
                                                <PartDisplay part={part} variant="table" classes={this.props.classes} />
                                            )}
                                        </td>
                                        <td style={{width: '60px', textAlign: 'center'}}>{formatQuantity(assemblyPart.quantity)}</td>
                                        <td>{formatBomUnitCostDisplay(assemblyPart, expandedPart)}</td>
                                        <td>{formatBomExtendedCostDisplay(assemblyPart, expandedPart, assemblyPart.quantity)}</td>
                                    </tr>
                                );
                            });
                        }
                    }
                });

                assemblyListHtml.push(
                    <div style={{marginTop: 20}}>
                        {this.renderBomMemo(subAssemblyMaybe.bomNotes)}
                        <div className="parts-table-container">
                            <table className="parts-table" style={{minWidth: '100%'}}>
                            <thead>
                            <tr>
                                <th style={{width: '30px'}}></th>
                                <th style={{width: 'auto', minWidth: '200px'}}>Part Number + Description</th>
                                <th style={{width: '60px'}}>Qty</th>
                                <th>Unit Cost</th>
                                <th>Ext. Cost</th>
                            </tr>
                            </thead>
                                <tbody>
                                {rowsHtml}
                                </tbody>
                            </table>
                        </div>
                        <div style={{margin: '20px 0'}}>
                            <b>Total Part Cost: ${totalPartCost.toFixed(2)}</b>
                        </div>
                    </div>
                );
            }
        }

        return (
            <DashboardTemplate title="Bill Of Materials" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root" style={{display: 'flex', flexDirection: 'row', margin: '80px auto 20px', maxWidth: 1200}}>
                    <div className="container" style={{maxWidth: 900, margin: '0 auto'}}>
                        <Autocomplete autoHighlight
                            id="ddlAssembly"
                            options={assemblyOptions}
                            onChange={this.handleAssemblyChange}
                            value={this.state.selectedAssembly}
                            getOptionLabel={(option) => option.title}
                            getOptionSelected={(option) => option.value === this.state.selectedAssembly}
                            className="autocomplete-dropdown"
                            style={{maxWidth: 600}}
                            renderInput={(params) => <TextField {...params} inputProps={{...params.inputProps}} label="Assembly" autoComplete="new-password" variant="outlined" />}
                        />
                        {assemblyListHtml}
                    </div>
                    {assemblyCostAnalysisHtml}
                </div>
            </DashboardTemplate>
        );
    }
}

let globalProps;

BillOfMaterials.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(BillOfMaterials);
